/**
 * Pure view-model of the end-of-run "Altar Offering" reveal (DOM-free, unit-tested in
 * offeringModel.test.ts): the payout split into lines (completion, world finds, Daily Offering,
 * first clear, weekly), Sigil callouts, the running totals, and the count-up schedule the DOM
 * animates (each line lands in turn, its numbers tally up, the totals follow).
 */
import { type ResourceBundle, type RunPayout, type VaultOptionKind, WEEKLY_RITUAL_RUNS } from '@keystone/progression';
import { type ResourceAmount, SCALE_LABEL, bundleEntries } from '../forge/resourceText';
import { SLOT_LABEL } from '../ui/itemText';

export type OfferingLineId = 'base' | 'world' | 'daily' | 'bracket' | 'weekly';

export interface OfferingLine {
  id: OfferingLineId;
  title: string;
  subtitle: string;
  entries: ResourceAmount[];
  /** Big callout on the line ("+2 ALTAR SIGILS"), or null. */
  callout: string | null;
  tone: 'base' | 'bonus' | 'sigil' | 'world';
  /** +world-resources: "Found in the dungeon" stat chips (smashed / caches / nodes), revealed one by one. */
  stats?: string[];
}

export interface OfferingInput {
  payout: RunPayout;
  timed: boolean;
  completed: boolean;
  level: number;
  chest: 0 | 1 | 2 | 3;
  vaultReady?: boolean;
  vaultAutoClaimed?: VaultOptionKind | null;
}

const sigilWord = (n: number): string => `${n} Altar Sigil${n === 1 ? '' : 's'}`;

/** The reveal lines, in the order they land. Empty lines are skipped. */
export function offeringLines(o: OfferingInput): OfferingLine[] {
  const pay = o.payout;
  const out: OfferingLine[] = [];
  const base = bundleEntries(pay.base);
  if (base.length) {
    const chest = o.chest > 0 ? ` · ${'+'.repeat(o.chest)} chest` : '';
    out.push({
      id: 'base',
      title: o.timed ? 'Timed clear' : o.completed ? 'Depleted clear' : 'Run',
      subtitle: `+${o.level} key${chest} · ${SCALE_LABEL[pay.grade]} Scales`,
      entries: base,
      callout: null,
      tone: 'base',
    });
  }
  const wl = worldLine(pay);
  if (wl) out.push(wl);
  if (pay.daily) {
    const entries = bundleEntries({ ...pay.daily.bonus, sigils: pay.daily.sigils });
    out.push({
      id: 'daily',
      title: 'Daily Offering',
      subtitle: `First clear of the day · Scales doubled · Favored slot today: ${SLOT_LABEL[pay.daily.favoredSlot]}`,
      entries,
      callout: `+${sigilWord(pay.daily.sigils)}${pay.daily.sigils > 1 ? ' (banked days)' : ''}`,
      tone: 'sigil',
    });
  }
  if (pay.bracket) {
    out.push({
      id: 'bracket',
      title: 'First timed clear',
      subtitle: `${SCALE_LABEL[pay.bracket.grade]} bracket of this dungeon`,
      entries: bundleEntries(pay.bracket.bundle),
      callout: (pay.bracket.bundle.sigils ?? 0) > 0 ? `+${sigilWord(pay.bracket.bundle.sigils!)}` : null,
      tone: 'sigil',
    });
  }
  if (pay.weekly) {
    const w = pay.weekly;
    out.push({
      id: 'weekly',
      title: 'Weekly Ritual',
      subtitle: w.ready ? 'Ready! Claim the Ritual Cache on the Tier Sets screen.' : `${Math.min(w.runs, WEEKLY_RITUAL_RUNS)}/${WEEKLY_RITUAL_RUNS} runs this week`,
      entries: [],
      callout: w.ready ? 'RITUAL READY' : null,
      tone: w.ready ? 'sigil' : 'bonus',
    });
  }
  return out;
}

/**
 * +world-resources: the "Found in the dungeon" line: what the player picked up (its own staged
 * reveal: chips land one by one) and what they broke / opened. Shown when anything was found or
 * broken, even on an abandoned key (world finds are always paid).
 */
export function worldLine(pay: RunPayout): OfferingLine | null {
  const entries = bundleEntries(pay.world);
  const ws = pay.worldStats;
  const stats: string[] = [];
  if (ws) {
    if (ws.breakables > 0) stats.push(`${ws.broken}/${ws.breakables} smashed`);
    if (ws.caches > 0) stats.push(`${ws.cachesOpened}/${ws.caches} hidden caches`);
    if (ws.nodes > 0 && ws.nodesBroken > 0) stats.push(`${ws.nodesBroken}/${ws.nodes} reagent nodes`);
  }
  const touched = !!ws && ws.broken + ws.cachesOpened + ws.nodesBroken > 0;
  if (!entries.length && !touched) return null;
  const all = !!ws && ws.caches > 0 && ws.cachesOpened >= ws.caches;
  return {
    id: 'world',
    title: 'Found in the dungeon',
    subtitle: entries.length ? 'Picked up from breakables, hidden caches and reagent nodes' : 'You smashed things, but nothing fell out this time',
    entries,
    callout: all ? 'EVERY CACHE FOUND' : null,
    tone: 'world',
    stats,
  };
}

/** Every Sigil this run paid, as callouts for the banner. */
export function sigilCallouts(pay: RunPayout): string[] {
  const out: string[] = [];
  if (pay.daily && pay.daily.sigils > 0) out.push(`Daily Offering: +${sigilWord(pay.daily.sigils)}`);
  if (pay.bracket && (pay.bracket.bundle.sigils ?? 0) > 0) out.push(`First ${SCALE_LABEL[pay.bracket.grade]} clear: +${sigilWord(pay.bracket.bundle.sigils!)}`);
  return out;
}

/** Sigils this run paid in total. */
export const sigilsEarned = (pay: RunPayout): number => (pay.daily?.sigils ?? 0) + (pay.bracket?.bundle.sigils ?? 0);

/** Totals row (what went into the wallet), in display order. */
export const offeringTotals = (pay: RunPayout): ResourceAmount[] => bundleEntries(pay.total);

/** Companion satchels line ("Each companion: +31 Fangsteel, +9 Ashen"), or null. */
export function satchelLine(pay: RunPayout): string | null {
  const e = bundleEntries(pay.satchel as ResourceBundle);
  if (!e.length) return null;
  return `Each companion’s satchel: ${e.map((a) => `+${a.n} ${a.key === 'fangsteel' ? 'Fangsteel' : SCALE_LABEL[a.key.slice(6) as keyof typeof SCALE_LABEL]}`).join(', ')}`;
}

// ---------------------------------------------------------------------------
// Count-up schedule
// ---------------------------------------------------------------------------

export interface TallyStep {
  line: number;
  /** When the line lands (ms from the start). */
  at: number;
  /** How long its numbers count up. */
  countMs: number;
  /** +world-resources: chip i starts counting at `at + i * stagger` (0 = all together). */
  stagger: number;
}

export interface TallySchedule {
  steps: TallyStep[];
  /** When the totals row starts counting. */
  totalsAt: number;
  totalsMs: number;
  /** Everything settled. */
  end: number;
}

/**
 * When each line lands and counts. Bonus / Sigil lines get a longer beat so they read as events.
 * Reduced motion collapses it to near-instant.
 */
export function tallySchedule(lines: readonly OfferingLine[], opts: { reducedMotion?: boolean } = {}): TallySchedule {
  const k = opts.reducedMotion ? 0.25 : 1;
  let at = Math.round(250 * k);
  const steps: TallyStep[] = [];
  lines.forEach((l, i) => {
    // +world-resources: the world line's chips land one by one (a loot-bag spill), each counting fast.
    const world = l.tone === 'world';
    const countMs = Math.round((world ? 380 : l.tone === 'base' ? 600 : 750) * k);
    const stagger = world ? Math.round(170 * k) : 0;
    steps.push({ line: i, at, countMs, stagger });
    at += countMs + stagger * Math.max(0, l.entries.length - 1) + Math.round((l.tone === 'sigil' ? 450 : world ? 320 : 200) * k);
  });
  const totalsMs = Math.round(500 * k);
  return { steps, totalsAt: at, totalsMs, end: at + totalsMs };
}

/** Count-up value at t of a tally that runs `dur` ms from `start` (ease-out, integers, exact at the end). */
export function countAt(n: number, start: number, dur: number, t: number): number {
  if (t <= start) return 0;
  if (dur <= 0 || t >= start + dur) return n;
  const p = (t - start) / dur;
  return Math.floor(n * (1 - Math.pow(1 - p, 3)));
}

// ---------------------------------------------------------------------------
// Reward screen: one row of at most 6 slots
// ---------------------------------------------------------------------------

export type RewardSlotKey = ResourceAmount['key'] | 'more';

export interface RewardSlot {
  key: RewardSlotKey;
  n: number;
  /** Special source strip under the icon ("First timed clear", "Daily", "Weekly", "Vault"). */
  tag: string | null;
  /** The folded resources of the "+ more" slot (its tooltip). */
  more?: ResourceAmount[];
}

export interface RewardSlotsInput {
  payout: RunPayout;
  gold: number;
  /** Caches the UI claimed automatically after the run (vault, Weekly Ritual). */
  vault?: ResourceBundle | null;
  weekly?: ResourceBundle | null;
}

const MAIN_KEYS = new Set(['fangsteel', 'venom', 'sigils']);

/**
 * The reward row: Fangsteel, one Scale grade (the run's), Venom, Sigils, Gold, and one "+ more"
 * slot folding everything else (other grades, Moltskin, Eye fragments, Blessed). At most 6.
 * Auto-claimed vault / weekly caches are added in. The Sigil slot carries the source tag.
 */
export function rewardSlots(i: RewardSlotsInput): RewardSlot[] {
  const sum = new Map<ResourceAmount['key'], number>();
  for (const b of [i.payout.total, i.vault ?? null, i.weekly ?? null]) for (const a of bundleEntries(b)) sum.set(a.key, (sum.get(a.key) ?? 0) + a.n);
  if (i.gold > 0) sum.set('gold', (sum.get('gold') ?? 0) + i.gold);
  // The one Scale grade shown: the run's grade if paid, else the biggest.
  const scales = [...sum.entries()].filter(([k]) => k.startsWith('scale.'));
  const runScale = `scale.${i.payout.grade}` as ResourceAmount['key'];
  const shownScale = sum.has(runScale) ? runScale : scales.sort((a, b) => b[1] - a[1])[0]?.[0];
  const pay = i.payout;
  const sigilTag = (pay.bracket?.bundle.sigils ?? 0) > 0 ? 'First timed clear' : (pay.daily?.sigils ?? 0) > 0 ? 'Daily' : (i.weekly?.sigils ?? 0) > 0 ? 'Weekly' : (i.vault?.sigils ?? 0) > 0 ? 'Vault' : null;
  const out: RewardSlot[] = [];
  const more: ResourceAmount[] = [];
  const order: ResourceAmount['key'][] = ['fangsteel', 'scale.ashen', 'scale.jade', 'scale.gilded', 'scale.eternal', 'venom', 'sigils', 'gold', 'moltskin', 'eyeFragments', 'blessed'];
  for (const key of order) {
    const n = sum.get(key) ?? 0;
    if (n <= 0) continue;
    if (MAIN_KEYS.has(key) || key === 'gold' || key === shownScale) out.push({ key, n, tag: key === 'sigils' ? sigilTag : null });
    else more.push({ key, n });
  }
  if (more.length) out.push({ key: 'more', n: more.length, tag: null, more });
  return out.slice(0, 6);
}
